import { useCallback, useEffect, useState } from 'react';
import {
  resolveScopeEditorPersistenceState,
  type ScopeEditorPersistenceState,
  type ScopeEditorPersistenceStateUpdater,
} from '../lib/scopeEditorPersistence';
import {
  DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES,
  DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES,
} from '../../../shared/config/mappingScopeEditorFieldDefaults';
import {
  PROCESS_CONDITIONS_EDITOR_STORAGE_KEY,
  PROJECT_PROPERTIES_EDITOR_STORAGE_KEY,
  subscribeToMappingScopeEditorProfileState,
  loadMappingScopeEditorState,
  saveMappingScopeEditorState,
} from '../lib/mappingScopeEditorStorage';

export interface UseMappingScopeEditorPersistenceResult {
  processConditionsEditorState: ScopeEditorPersistenceState;
  handleProcessConditionsEditorStateChange: (nextStateOrUpdater: ScopeEditorPersistenceStateUpdater) => void;
  projectPropertiesEditorState: ScopeEditorPersistenceState;
  handleProjectPropertiesEditorStateChange: (nextStateOrUpdater: ScopeEditorPersistenceStateUpdater) => void;
}

export function useMappingScopeEditorPersistence(): UseMappingScopeEditorPersistenceResult {
  const [processConditionsEditorState, setProcessConditionsEditorState] =
    useState<ScopeEditorPersistenceState>(() =>
      loadMappingScopeEditorState(
        PROCESS_CONDITIONS_EDITOR_STORAGE_KEY,
        DEFAULT_PROCESS_CONDITIONS_PCF_ATTRIBUTE_NAMES
      )
    );
  const [projectPropertiesEditorState, setProjectPropertiesEditorState] =
    useState<ScopeEditorPersistenceState>(() =>
      loadMappingScopeEditorState(
        PROJECT_PROPERTIES_EDITOR_STORAGE_KEY,
        DEFAULT_PROJECT_PROPERTIES_PCF_ATTRIBUTE_NAMES
      )
    );

  const handleProcessConditionsEditorStateChange = useCallback(
    (nextStateOrUpdater: ScopeEditorPersistenceStateUpdater) => {
      setProcessConditionsEditorState((prevState) => {
        const nextState = resolveScopeEditorPersistenceState(prevState, nextStateOrUpdater);
        saveMappingScopeEditorState(PROCESS_CONDITIONS_EDITOR_STORAGE_KEY, nextState);
        return nextState;
      });
    },
    []
  );

  const handleProjectPropertiesEditorStateChange = useCallback(
    (nextStateOrUpdater: ScopeEditorPersistenceStateUpdater) => {
      setProjectPropertiesEditorState((prevState) => {
        const nextState = resolveScopeEditorPersistenceState(prevState, nextStateOrUpdater);
        saveMappingScopeEditorState(PROJECT_PROPERTIES_EDITOR_STORAGE_KEY, nextState);
        return nextState;
      });
    },
    []
  );

  useEffect(
    () =>
      subscribeToMappingScopeEditorProfileState((nextState) => {
        setProcessConditionsEditorState(nextState.processConditions);
        setProjectPropertiesEditorState(nextState.projectProperties);
      }),
    []
  );

  return {
    processConditionsEditorState,
    handleProcessConditionsEditorStateChange,
    projectPropertiesEditorState,
    handleProjectPropertiesEditorStateChange,
  };
}
